<template>
  <div class="q-gutter-xs justify-around row">
    <q-btn
        :dark="isDarkModeGlobal"
        @click="downloadDialog()"
        color="primary"
        icon="download"
        round
    >
      <q-tooltip>
        Export calculator settings
      </q-tooltip>
    </q-btn>
    <q-file
        v-model="file"
        ref="fileInput"
        clearable
        outlined
        dense
        borderless
        :dark="isDarkModeGlobal"
        label="Select file"
    >
      <template #prepend>
        <q-btn
            dense
            color="primary"
            text-color="white"
            icon="upload"
            round
            @click="openFileDialog"
        >
          <q-tooltip>
            Import a Yml or Json file
          </q-tooltip>
        </q-btn>

      </template>
      <template #append>
        <q-btn
            v-if="canUpload && !importSuccessShow"
            color="primary"
            icon="send"
            @click="handleFileUpload"
            dense
        >
          Import
        </q-btn>
        <q-icon
            v-if="importSuccessShow"
            name="check"
            color="green"
        />
      </template>
    </q-file>
    <q-btn
        flat
        color="primary"
        icon="refresh"
        @click="resetParameters"
    >
      <q-tooltip>Reset all parameters to default values</q-tooltip>
    </q-btn>
  </div>
    <q-dialog
        :dark="isDarkModeGlobal"
        v-model="settingsExportConfirmationDialog"
        transition-show="scale"
        transition-hide="scale"
      >
        <q-card
          :dark="isDarkModeGlobal"
          style="width: 600px"
        >
          <q-card-section>
            <div class="text-h6">
              <q-icon name="download" /> Download TUS-calculator settings
            </div>
          </q-card-section>

          <q-card-section
            class="text-center"
          >
            <q-input
              outlined
              :dark="isDarkModeGlobal"
              v-model="filename"
              label="Name"
            >
              <template #append>
                <q-btn
                  class="q-ma-sm q-pa-sm"
                  icon="download"
                  push
                  :label="`json`"
                  dense
                  color="primary"
                  @click="transactionStore.exportState('json', filename)"
                >
                  <q-tooltip>
                    Download settings as JSON file
                  </q-tooltip>
                </q-btn>
                <q-btn
                  class="q-ma-sm q-pa-sm"
                  icon="download"
                  push
                  :label="`YAML`"
                  dense
                  color="primary"
                  @click="transactionStore.exportState('yml', filename)"
                >
                  <q-tooltip>
                    Download settings as YAML file
                  </q-tooltip>
                </q-btn>
              </template>
            </q-input>
          </q-card-section>
          <q-card-actions
            align="right"
            class="text-teal"
          >
            <q-btn
              flat
              label="Close"
              color="primary"
              v-close-popup
            />
          </q-card-actions>
        </q-card>
      </q-dialog>

</template>
<script setup lang="ts">
import {storeToRefs} from "pinia"
import {useCalculatorStore} from '../stores/calculator.store'
import {useTransactionStore} from '../stores/transaction.store'
import {computed, nextTick, ref} from "vue"
const calculatorStore = useCalculatorStore()
const transactionStore = useTransactionStore()
const {
  isDarkModeGlobal,
  importingData,
} = storeToRefs(calculatorStore)

const file = ref(null)
const fileInput = ref(null)
const canUpload = computed(() => file.value !== null)

function openFileDialog() {
  fileInput.value.pickFiles()
}

const fileType = computed(() => file.value.type)
const isYaml = computed(() => fileType.value === "application/x-yaml"
    || file.value.name.endsWith(".yml")
    || file.value.name.endsWith(".yaml"))
const isJson = computed(() => fileType.value === "application/json")
const importSuccessShow = ref(false)
const settingsExportConfirmationDialog = ref(false)
const filename = ref('data')

function resetParameters() {
  const previousDarkModeGlobalValue = isDarkModeGlobal.value
  calculatorStore.$reset()
  calculatorStore.restartWorker()
  calculatorStore.$patch({
    confirmConditionsOfUse: true,
    isDarkModeGlobal: previousDarkModeGlobalValue
  })
  calculatorStore.initWorkers()
  calculatorStore.recalculate()
}



function downloadDialog() {
  settingsExportConfirmationDialog.value = true
}

function parseSimpleYAML(yamlString: string) {
  const lines = yamlString.split(/\r?\n/g);
  const result = {};

  lines.forEach(line => {
    if (line.trim() === '' || line.trim().startsWith('#')) return;

    // Use a regex to split only on the first colon to preserve colons in values
    const match = line.match(/^([^:]+):(.+)$/);
    if (match) {
        const key = match[1].trim();
        const value = match[2].trim();
        result[key] = value; // Keep as string; mapToStateKeys handles types
    }
  });
  return result;
}

async function handleFileUpload() {
  if (!file.value) {
    console.error('No file selected');
    return;
  }

  try {
    const fileContent = await readFileAsText(file.value);
    parseAndImportFile(fileContent);
  } catch (error) {
    console.error('Error during file upload:', error.message);
  }
}

// Helper function to read file content as text
function readFileAsText(file: any) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => resolve(e.target.result)
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsText(file)
  })
}

// Helper function to handle file format parsing
function parseAndImportFile(content: any) {
  try {
    if (typeof content !== 'string') throw new Error('Invalid file content format')
    let jsonObject;
    if (isJson.value) {
      jsonObject = JSON.parse(content)
    } else if (isYaml.value) {
      jsonObject = parseSimpleYAML(content)
    } else {
      throw new Error('Unsupported format, please use JSON or YAML')
    }
    importToState(jsonObject)
  } catch (error) {
    console.error('Error parsing file:', error.message)
  }
}

async function importToState(jsonObject: any) {
  try {
    importingData.value = true

    const newState = transactionStore.mapToStateKeys(jsonObject);
    if (transactionStore.validateImportJson(newState)) {
      calculatorStore.$patch(newState)
      await nextTick()
      importSuccess()
    }
  } catch {
    console.error('import failed')
    importingData.value = false
  }
}

 function importSuccess() {
  importSuccessShow.value = true
  importingData.value = false

  calculatorStore.recalculate()

  setTimeout(() => {
    importSuccessShow.value = false
    file.value = null

  }, 1000)
}
</script>
